/**
 * ============================================================================
 * ADD MEMBER MODAL v1.1.0 - Familienmitglied hinzufügen
 * ============================================================================
 *
 * Modal zum Hinzufügen neuer Familienmitglieder (ohne Logto-Account)
 * z.B. Kinder, Großeltern, etc.
 *
 * Features:
 * - Name eingeben
 * - Rolle auswählen (Kind, Elternteil, Großeltern, Andere)
 * - Farbe auswählen
 * - Kontaktdaten: Telefon, E-Mail
 * - Optionale Felder: Arbeitsort, Arbeitszeiten, Geburtsdatum
 *
 * @version 1.1.0
 * @date 2026-01-21
 * ============================================================================
 */

import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';

console.log('➕ AddMemberModal.js v1.1.0 loaded - with Phone & Email');

// Rollen-Optionen
const ROLE_OPTIONS = [
  { value: 'kind', label: 'Kind', icon: '👶' },
  { value: 'elternteil', label: 'Elternteil', icon: '👨' },
  { value: 'grosseltern', label: 'Großeltern', icon: '👴' },
  { value: 'andere', label: 'Andere', icon: '👤' }
];

// Farb-Optionen
const COLOR_OPTIONS = [
  { value: '#4A90A4', label: 'Teal' },
  { value: '#6BA893', label: 'Grün' },
  { value: '#A855F7', label: 'Lila' },
  { value: '#3B82F6', label: 'Blau' },
  { value: '#22C55E', label: 'Hellgrün' },
  { value: '#F59E0B', label: 'Orange' },
  { value: '#EC4899', label: 'Pink' },
  { value: '#EF4444', label: 'Rot' }
];

function AddMemberModal({ isOpen, onClose, onSave, isLoading }) {
  const { isDark } = useTheme();
  const styles = getStyles(isDark);

  const [formData, setFormData] = useState({
    name: '',
    role: 'kind',
    color: '#4A90A4',
    phone: '',
    email: '',
    location: '',
    schedule: '',
    birthDate: ''
  });

  const [errors, setErrors] = useState({});
  const [isHovered, setIsHovered] = useState(null);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when field changes
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }
    if (formData.name.trim().length < 2) {
      newErrors.name = 'Name muss mindestens 2 Zeichen haben';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave({
        ...formData,
        name: formData.name.trim(),
        // Calculate age if birthDate provided
        age: formData.birthDate ? calculateAge(formData.birthDate) : null
      });
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      role: 'kind',
      color: '#4A90A4',
      phone: '',
      email: '',
      location: '',
      schedule: '',
      birthDate: ''
    });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div style={styles.overlay} onClick={handleClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <h2 style={styles.title}>
            <span>➕</span> Familienmitglied hinzufügen
          </h2>
          <button
            style={styles.closeBtn}
            onClick={handleClose}
            onMouseEnter={() => setIsHovered('close')}
            onMouseLeave={() => setIsHovered(null)}
          >
            ✕
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={styles.body}>
            {/* Name */}
            <div style={styles.formGroup}>
              <label style={styles.label}>
                Name <span style={styles.required}>*</span>
              </label>
              <input
                type="text"
                style={{
                  ...styles.input,
                  ...(errors.name ? styles.inputError : {})
                }}
                placeholder="z.B. Emma, Luca, Oma..."
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                autoFocus
              />
              {errors.name && (
                <span style={styles.errorText}>{errors.name}</span>
              )}
            </div>

            {/* Rolle */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Rolle</label>
              <div style={styles.roleGrid}>
                {ROLE_OPTIONS.map(role => (
                  <button
                    key={role.value}
                    type="button"
                    style={{
                      ...styles.roleBtn,
                      ...(formData.role === role.value ? styles.roleBtnActive : {}),
                      ...(isHovered === `role-${role.value}` && formData.role !== role.value ? styles.roleBtnHover : {})
                    }}
                    onClick={() => handleChange('role', role.value)}
                    onMouseEnter={() => setIsHovered(`role-${role.value}`)}
                    onMouseLeave={() => setIsHovered(null)}
                  >
                    <span style={styles.roleIcon}>{role.icon}</span>
                    <span>{role.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Farbe */}
            <div style={styles.formGroup}>
              <label style={styles.label}>Farbe</label>
              <div style={styles.colorGrid}>
                {COLOR_OPTIONS.map(color => (
                  <button
                    key={color.value}
                    type="button"
                    style={{
                      ...styles.colorBtn,
                      background: color.value,
                      ...(formData.color === color.value ? styles.colorBtnActive : {}),
                      ...(isHovered === `color-${color.value}` ? { transform: 'scale(1.1)' } : {})
                    }}
                    onClick={() => handleChange('color', color.value)}
                    onMouseEnter={() => setIsHovered(`color-${color.value}`)}
                    onMouseLeave={() => setIsHovered(null)}
                    title={color.label}
                  >
                    {formData.color === color.value && '✓'}
                  </button>
                ))}
              </div>
            </div>

            {/* Kontakt-Sektion */}
            <div style={styles.contactSection}>
              <div style={styles.sectionHeader}>
                <span>📞</span> Kontaktdaten
              </div>
              <div style={styles.contactGrid}>
                {/* Telefon */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>Telefon</label>
                  <input
                    type="tel"
                    style={styles.input}
                    placeholder="z.B. +49 170 1234567"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                  />
                </div>
                {/* Email */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>E-Mail</label>
                  <input
                    type="email"
                    style={styles.input}
                    placeholder="z.B. name@example.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Optionale Felder - Collapsed by default */}
            <details style={styles.optionalSection}>
              <summary style={styles.optionalSummary}>
                📋 Optionale Angaben
              </summary>

              <div style={styles.optionalContent}>
                {/* Geburtsdatum */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>Geburtsdatum</label>
                  <input
                    type="date"
                    style={styles.input}
                    value={formData.birthDate}
                    onChange={(e) => handleChange('birthDate', e.target.value)}
                  />
                </div>

                {/* Arbeitsort/Schule */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>Ort (Schule/Arbeit/Kita)</label>
                  <input
                    type="text"
                    style={styles.input}
                    placeholder="z.B. Grundschule, Kindergarten..."
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                  />
                </div>

                {/* Zeiten */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>Zeiten</label>
                  <input
                    type="text"
                    style={styles.input}
                    placeholder="z.B. 08:00-13:00"
                    value={formData.schedule}
                    onChange={(e) => handleChange('schedule', e.target.value)}
                  />
                </div>
              </div>
            </details>
          </div>

          {/* Footer */}
          <div style={styles.footer}>
            <button
              type="button"
              style={{
                ...styles.btnSecondary,
                ...(isHovered === 'cancel' ? styles.btnSecondaryHover : {})
              }}
              onClick={handleClose}
              onMouseEnter={() => setIsHovered('cancel')}
              onMouseLeave={() => setIsHovered(null)}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              style={{
                ...styles.btnPrimary,
                ...(isHovered === 'save' ? styles.btnPrimaryHover : {}),
                opacity: isLoading ? 0.7 : 1
              }}
              disabled={isLoading}
              onMouseEnter={() => setIsHovered('save')}
              onMouseLeave={() => setIsHovered(null)}
            >
              {isLoading ? (
                <>
                  <span style={styles.spinner} />
                  Speichere...
                </>
              ) : (
                <>
                  <span>➕</span>
                  Hinzufügen
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Animation Keyframes */}
      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

/**
 * Calculate age from birthdate
 */
function calculateAge(birthDate) {
  const today = new Date();
  const birth = new Date(birthDate);
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
}

/**
 * Styles
 */
function getStyles(isDark) {
  const colors = isDark ? {
    bg: '#1F2937',
    bgSecondary: '#374151',
    text: '#F9FAFB',
    textSecondary: '#D1D5DB',
    textMuted: '#9CA3AF',
    border: '#4B5563',
    accent: '#4A90A4',
    error: '#EF4444'
  } : {
    bg: '#FFFFFF',
    bgSecondary: '#F0F9FF',
    text: '#1F2937',
    textSecondary: '#374151',
    textMuted: '#6B7280',
    border: '#B8D4DC',
    accent: '#4A90A4',
    error: '#EF4444'
  };

  return {
    overlay: {
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '1rem'
    },
    modal: {
      background: colors.bg,
      width: '100%',
      maxWidth: '480px',
      maxHeight: '90vh',
      overflowY: 'auto',
      borderRadius: '20px',
      boxShadow: '0 20px 50px rgba(0, 0, 0, 0.3)',
      animation: 'slideUp 0.3s ease'
    },
    header: {
      padding: '1.25rem 1.5rem',
      borderBottom: `2px solid ${colors.border}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      position: 'sticky',
      top: 0,
      background: colors.bg,
      zIndex: 1
    },
    title: {
      fontSize: '1.25rem',
      fontWeight: 700,
      color: colors.text,
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      margin: 0
    },
    closeBtn: {
      background: isDark ? '#7F1D1D' : '#FEE2E2',
      border: 'none',
      width: '40px',
      height: '40px',
      borderRadius: '12px',
      fontSize: '1.25rem',
      color: '#EF4444',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s'
    },
    body: {
      padding: '1.5rem'
    },
    formGroup: {
      marginBottom: '1.25rem'
    },
    label: {
      display: 'block',
      fontSize: '0.875rem',
      fontWeight: 600,
      color: colors.textSecondary,
      marginBottom: '0.5rem'
    },
    required: {
      color: colors.error,
      marginLeft: '2px'
    },
    input: {
      width: '100%',
      padding: '0.75rem 1rem',
      border: `2px solid ${colors.border}`,
      borderRadius: '12px',
      fontSize: '1rem',
      color: colors.text,
      background: colors.bgSecondary,
      transition: 'all 0.2s',
      boxSizing: 'border-box',
      outline: 'none'
    },
    inputError: {
      border: `2px solid ${colors.error}`
    },
    errorText: {
      color: colors.error,
      fontSize: '0.8rem',
      marginTop: '4px',
      display: 'block'
    },
    roleGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '0.5rem'
    },
    roleBtn: {
      padding: '0.75rem 0.5rem',
      border: `2px solid ${colors.border}`,
      borderRadius: '12px',
      background: colors.bg,
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '0.25rem',
      fontSize: '0.75rem',
      fontWeight: 600,
      color: colors.textMuted,
      transition: 'all 0.2s'
    },
    roleBtnActive: {
      border: `2px solid ${colors.accent}`,
      background: isDark ? '#1E3A4A' : '#E8F4F8',
      color: colors.accent
    },
    roleBtnHover: {
      border: `2px solid ${colors.border}`,
      background: colors.bgSecondary
    },
    roleIcon: {
      fontSize: '1.5rem'
    },
    colorGrid: {
      display: 'flex',
      gap: '0.5rem',
      flexWrap: 'wrap'
    },
    colorBtn: {
      width: '40px',
      height: '40px',
      borderRadius: '10px',
      border: '3px solid transparent',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontWeight: 'bold',
      fontSize: '1rem',
      transition: 'all 0.2s'
    },
    colorBtnActive: {
      border: `3px solid ${colors.text}`,
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
    },
    contactSection: {
      marginBottom: '1.25rem',
      padding: '1rem',
      background: isDark ? '#1E3A4A' : '#E8F4F8',
      borderRadius: '12px',
      border: `1px solid ${colors.border}`
    },
    sectionHeader: {
      fontSize: '0.9rem',
      fontWeight: 600,
      color: colors.accent,
      marginBottom: '0.75rem',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem'
    },
    contactGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: '0.75rem'
    },
    optionalSection: {
      marginTop: '1rem',
      border: `1px solid ${colors.border}`,
      borderRadius: '12px',
      overflow: 'hidden'
    },
    optionalSummary: {
      padding: '12px 16px',
      background: colors.bgSecondary,
      cursor: 'pointer',
      fontWeight: 600,
      color: colors.textSecondary,
      fontSize: '0.9rem',
      listStyle: 'none',
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    optionalContent: {
      padding: '16px'
    },
    footer: {
      padding: '1rem 1.5rem',
      borderTop: `2px solid ${colors.border}`,
      display: 'flex',
      gap: '0.75rem',
      justifyContent: 'flex-end',
      position: 'sticky',
      bottom: 0,
      background: colors.bg
    },
    btnPrimary: {
      padding: '0.875rem 1.5rem',
      background: 'linear-gradient(135deg, #4A90A4 0%, #6BA893 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '12px',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      transition: 'all 0.2s',
      boxShadow: '0 2px 8px rgba(74, 144, 164, 0.3)'
    },
    btnPrimaryHover: {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(74, 144, 164, 0.4)'
    },
    btnSecondary: {
      padding: '0.875rem 1.5rem',
      background: colors.bg,
      color: colors.textMuted,
      border: `2px solid ${colors.border}`,
      borderRadius: '12px',
      fontSize: '1rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s'
    },
    btnSecondaryHover: {
      background: colors.bgSecondary,
      border: `2px solid ${colors.textMuted}`
    },
    spinner: {
      width: '16px',
      height: '16px',
      border: '2px solid white',
      borderTopColor: 'transparent',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
      display: 'inline-block'
    }
  };
}

export default AddMemberModal;
